/**
 * IMPORTANT FUNCTION
 *
 * This function fetches the data from the database and updates the cache and returns the data
 *
 * @param {string} userId - The user's id
 * @param {string} updateItem - The item to update, if none is passed, the items are retrieved from the cache
 *
 * Called by VehicleMatch.js
 */

import { getAuthenticatedClient } from "../supabaseClient";

import useCache from "../store/useCache"; // Import Zustand store
import { fetchCityInfo } from "../api/cityInfo"; // Import fetchCityInfo function
import { fetchChargerData } from "../api/chargercostapi"; // Import fetchChargerData function

const fetchData = async (userId, updateItem) => {
  const state = useCache.getState();
  const supabase = getAuthenticatedClient();
  const shouldFetchAll = !updateItem;
  const shouldFetchNone = updateItem === "none";

  const shouldFetch = (item) =>
    !shouldFetchNone && (shouldFetchAll || updateItem === item);

  const promises = {
    fleetData: shouldFetch("fleetData")
      ? supabase.from("fleet data").select("*").eq("user_id", userId)
      : Promise.resolve(state.fleetData),
    advancedControls: shouldFetch("advancedControls")
      ? supabase.from("advanced controls").select("*").eq("id", userId)
      : Promise.resolve(state.advancedControls),
    controls: shouldFetch("controls")
      ? supabase.from("controls").select("*").eq("id", userId)
      : Promise.resolve(state.controls),
    phases: shouldFetch("phases")
      ? supabase.from("phases").select("*").eq("user_id", userId)
      : Promise.resolve(state.phases),
    chargerCost: shouldFetch("chargerCost")
      ? (async () => {
          // First, try to fetch from "charger costs" table
          const chargerCostsResponse = await fetchChargerData(userId);

          if (chargerCostsResponse.data) {
            // Use data from "charger costs" table
            return { error: null, data: [chargerCostsResponse.data] };
          }

          // Fall back to "default data" table's charger_costs column
          const { data, error } = await supabase
            .from("default data")
            .select("charger_costs")
            .eq("id", userId)
            .maybeSingle();

          // If no row exists or charger_costs is null, return empty array (matching original behavior)
          if (error || !data || !data.charger_costs) {
            return { error: null, data: [] };
          }

          // Extract charger_costs from the JSONB column
          // charger_costs is stored as an array, so we need to handle both array and object formats
          const chargerCostsArray = Array.isArray(data.charger_costs)
            ? data.charger_costs
            : [data.charger_costs];

          return { error: null, data: chargerCostsArray };
        })()
      : Promise.resolve(state.chargerCost),
    cityInfo: shouldFetch("cityInfo")
      ? fetchCityInfo()
      : Promise.resolve(state.cityInfo),
  };

  const [
    fleetDataResponse,
    advancedControlsResponse,
    controlsResponse,
    phasesResponse,
    chargerCostResponse,
    cityInfoResponse,
  ] = await Promise.all(Object.values(promises));

  const errors = [
    { name: "fleetData", error: fleetDataResponse.error },
    { name: "advancedControls", error: advancedControlsResponse.error },
    { name: "controls", error: controlsResponse.error },
    { name: "phases", error: phasesResponse.error },
    { name: "chargerCost", error: chargerCostResponse.error },
    { name: "cityInfo", error: cityInfoResponse.error },
  ].filter(({ error }) => error);

  if (errors.length) {
    errors.forEach(({ name, error }) => {
      throw new Error(`Error fetching ${name}: ${error.message}`);
    });
  }

  if (shouldFetchAll || updateItem === "fleetData")
    useCache.setState({ fleetData: fleetDataResponse });
  if (shouldFetchAll || updateItem === "advancedControls")
    useCache.setState({ advancedControls: advancedControlsResponse });
  if (shouldFetchAll || updateItem === "controls")
    useCache.setState({ controls: controlsResponse });
  if (shouldFetchAll || updateItem === "phases")
    useCache.setState({ phases: phasesResponse });
  if (shouldFetchAll || updateItem === "chargerCost")
    useCache.setState({ chargerCost: chargerCostResponse });
  if (shouldFetchAll || updateItem === "cityInfo")
    useCache.setState({ cityInfo: cityInfoResponse });

  return {
    fleetData: fleetDataResponse.data,
    advancedControls: advancedControlsResponse.data[0],
    controls: controlsResponse.data[0],
    phases: phasesResponse.data,
    chargerCost: chargerCostResponse.data[0],
    cityInfo: cityInfoResponse.data[0],
  };
};

export default fetchData;
